/**
 * Product Customizer - Decade Awards
 * Handles character counters, form validation, file uploads, and dynamic show/hide.
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initCharacterCounters();
    initApplyToAllLines();
    initRibbonPreview();
    initFileUpload();
    initFormValidation();
    initTextareaCounters();
    initVariantEngravingUpdate();
  }

  /**
   * Character counters for engraving inputs
   */
  function initCharacterCounters() {
    const engravingInputs = document.querySelectorAll('[data-engraving-input]');

    engravingInputs.forEach(input => {
      const wrapper = input.closest('.product-customizer__input-wrapper');
      const counter = wrapper?.querySelector('[data-char-count] [data-current]');

      if (!counter) return;

      const updateCounter = () => {
        const current = input.value.length;
        counter.textContent = current;

        // Add warning class if near limit
        const max = parseInt(input.maxLength, 10);
        const parent = counter.closest('.product-customizer__char-count');
        if (parent) {
          parent.classList.toggle('product-customizer__char-count--warning', current >= max * 0.9);
          parent.classList.toggle('product-customizer__char-count--limit', current >= max);
        }
      };

      input.addEventListener('input', updateCounter);
      updateCounter(); // Initial count
    });
  }

  /**
   * Apply to all / Lock — product page.
   * Lock (disabled inputs, "Lock" button) happens only when the user clicks "Apply to all", not when they type in all 3 fields.
   * Clicking "Lock" enables the inputs and shows "Apply to all" again.
   */
  function initApplyToAllLines() {
    const container = document.querySelector('.product-customizer__engraving');
    const btn = container?.querySelector('[data-apply-to-all-lines]');
    const inputs = container ? Array.from(container.querySelectorAll('[data-engraving-input]')) : [];
    if (!btn || inputs.length === 0) return;

    let isLocked = false;

    function getDistinctValues() {
      const values = new Set();
      inputs.forEach(input => {
        const val = (input.value || '').trim();
        if (val) values.add(val);
      });
      return values;
    }

    function setLocked(locked) {
      isLocked = locked;
      inputs.forEach(input => { input.disabled = locked; });
      btn.textContent = locked ? 'Lock' : 'Apply to all';
      btn.setAttribute('aria-label', locked ? 'Unlock and edit lines' : 'Apply same text to all lines');
      if (locked) {
        btn.disabled = false;
      } else {
        btn.disabled = getDistinctValues().size !== 1;
      }
    }

    /** When not locked: enable/disable "Apply to all" based on one distinct value. No auto-lock on typing. */
    function updateButtonState() {
      if (isLocked) return;
      const distinct = getDistinctValues();
      btn.disabled = distinct.size !== 1;
    }

    btn.addEventListener('click', () => {
      if (isLocked) {
        setLocked(false);
        return;
      }
      const distinct = getDistinctValues();
      if (distinct.size !== 1) return;
      const teamName = Array.from(distinct)[0];
      const maxLen = parseInt(container.dataset.charsPerLine, 10) || 999;
      const value = teamName.length > maxLen ? teamName.slice(0, maxLen) : teamName;
      inputs.forEach(input => {
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      setLocked(true);
    });

    inputs.forEach(input => {
      input.addEventListener('input', updateButtonState);
      input.addEventListener('change', updateButtonState);
    });
    updateButtonState();
  }

  /**
   * Textarea character counters (comments field)
   */
  function initTextareaCounters() {
    const textareas = document.querySelectorAll('.product-customizer__textarea');

    textareas.forEach(textarea => {
      const wrapper = textarea.closest('.product-customizer__comments');
      const counter = wrapper?.querySelector('.product-customizer__char-count [data-current]');

      if (!counter) return;

      const updateCounter = () => {
        counter.textContent = textarea.value.length;
      };

      textarea.addEventListener('input', updateCounter);
      updateCounter();
    });
  }

  /**
   * Ribbon dropdown preview
   */
  function initRibbonPreview() {
    const ribbonSelect = document.getElementById('ribbon-select');
    const preview = document.getElementById('ribbon-preview');
    const previewText = document.getElementById('ribbon-preview-text');

    if (!ribbonSelect || !preview || !previewText) return;

    ribbonSelect.addEventListener('change', () => {
      const value = ribbonSelect.value;
      if (value) {
        previewText.textContent = value;
        preview.hidden = false;
      } else {
        preview.hidden = true;
      }
    });
  }

  /**
   * File upload handling
   * File is uploaded natively by Shopify when the product form submits via FormData.
   * This JS only handles validation and UI feedback.
   */
  function initFileUpload() {
    const fileInput = document.getElementById('group-order-file');
    const uploadArea = document.getElementById('upload-area');
    const successDiv = document.getElementById('upload-success');
    const filenameSpan = document.getElementById('upload-filename');
    const removeBtn = document.getElementById('remove-file');

    if (!fileInput || !uploadArea) return;

    // Drag and drop handlers
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      uploadArea.addEventListener(eventName, preventDefaults, false);
    });

    function preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    ['dragenter', 'dragover'].forEach(eventName => {
      uploadArea.addEventListener(eventName, () => {
        uploadArea.classList.add('product-customizer__upload-area--dragover');
      });
    });

    ['dragleave', 'drop'].forEach(eventName => {
      uploadArea.addEventListener(eventName, () => {
        uploadArea.classList.remove('product-customizer__upload-area--dragover');
      });
    });

    uploadArea.addEventListener('drop', (e) => {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        fileInput.files = files;
        handleFile(files[0]);
      }
    });

    fileInput.addEventListener('change', () => {
      if (fileInput.files.length > 0) {
        handleFile(fileInput.files[0]);
      }
    });

    function handleFile(file) {
      // Validate file size (max 20MB — Shopify's limit)
      if (file.size > 20 * 1024 * 1024) {
        alert('File is too large. Maximum size is 20MB.');
        fileInput.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['.doc', '.docx', '.xls', '.xlsx', '.csv', '.pdf', '.txt', '.png', '.jpg', '.jpeg'];
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(extension)) {
        alert('Invalid file type. Please upload a document or image file.');
        fileInput.value = '';
        return;
      }

      // Show success — actual upload happens when form submits via FormData
      successDiv.hidden = false;
      filenameSpan.textContent = file.name;
    }

    // Remove file handler
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        fileInput.value = '';
        successDiv.hidden = true;
      });
    }
  }

  /**
   * Variant-aware engraving update
   * Reads per-variant engraving config (keyed by variant ID) and rebuilds
   * engraving fields whenever the customer selects a different size/variant.
   */
  function initVariantEngravingUpdate() {
    const dataEl = document.getElementById('variant-engraving-data');
    if (!dataEl) return;

    let variantData;
    try {
      variantData = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const byId    = variantData.byId    || {};
    const byTitle = variantData.byTitle || {};

    // If all variants share the same config, nothing to do
    const values = Object.values(byId);
    if (values.length <= 1) return;
    const allSame = values.every(v => v.lines === values[0].lines && v.chars === values[0].chars);
    if (allSame) return;

    const container = document.querySelector('.product-customizer__engraving');
    if (!container) return;

    function getConfigFromCheckedOptions() {
      // Build title from currently checked radios/selects (e.g. "Large" or "Red / Large")
      const parts = [];
      document.querySelectorAll('input[type="radio"]:checked').forEach(r => {
        if (!r.closest('[data-product-customizer]')) parts.push(r.value);
      });
      document.querySelectorAll('select').forEach(s => {
        if (!s.closest('[data-product-customizer]') && s.value) parts.push(s.value);
      });
      // Try full joined title first, then each part individually
      const title = parts.join(' / ');
      if (byTitle[title]) return byTitle[title];
      for (const part of parts) {
        if (byTitle[part]) return byTitle[part];
      }
      return null;
    }

    function getConfigFromVariantId() {
      const urlId = new URLSearchParams(window.location.search).get('variant');
      if (urlId && byId[urlId]) return byId[urlId];
      const inputId = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      if (inputId && byId[inputId]) return byId[inputId];
      return null;
    }

    function applyVariantEngraving() {
      // Try instant title-based lookup first (no async lag)
      const config = getConfigFromCheckedOptions() || getConfigFromVariantId();
      if (!config) return;
      rebuildEngravingFields(config.lines, config.chars);
      updateBulkModal(config.lines, config.chars);
    }

    function updateBulkModal(targetLines, targetChars) {
      const bulkModal = document.getElementById('bulk-engraving-modal');
      if (!bulkModal) return;
      // Update data attributes (for any future reads / re-connection)
      bulkModal.dataset.lineCount    = targetLines;
      bulkModal.dataset.charsPerLine = targetChars;
      // Use the dedicated method if available (re-renders open modal live)
      if (typeof bulkModal.updateConfig === 'function') {
        bulkModal.updateConfig(targetLines, targetChars);
      }
    }

    // Fires instantly when radio/select changes — title lookup needs no delay
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (input.type === 'radio' || input.tagName === 'SELECT') applyVariantEngraving();
    });

    // Dawn's custom variant:changed event (fires after Dawn updates hidden input)
    document.addEventListener('variant:changed', applyVariantEngraving);

    // Apply on page load for the default selected variant
    applyVariantEngraving();

    function rebuildEngravingFields(targetLines, targetChars) {
      const linesContainer = container.querySelector('.product-customizer__lines');
      if (!linesContainer) return;

      const currentWrappers = Array.from(linesContainer.querySelectorAll('.product-customizer__input-wrapper'));
      const currentLines = currentWrappers.length;
      const currentChars = currentWrappers[0] ? parseInt(currentWrappers[0].querySelector('input')?.maxLength, 10) : 0;

      // Nothing to do
      if (currentLines === targetLines && currentChars === targetChars) return;

      // Update container data attributes
      container.dataset.engravingLines = targetLines;
      container.dataset.charsPerLine   = targetChars;

      // Update header text
      const meta = container.querySelector('.product-customizer__meta');
      if (meta) {
        meta.textContent = `${targetLines} line${targetLines > 1 ? 's' : ''} \u2022 ${targetChars} chars each \u2022 FREE`;
      }

      // Remove extra line wrappers
      for (let i = currentLines; i > targetLines; i--) {
        currentWrappers[i - 1].remove();
      }

      // Add missing line wrappers
      for (let i = currentLines + 1; i <= targetLines; i++) {
        const wrapper = document.createElement('div');
        wrapper.className = 'product-customizer__input-wrapper';
        wrapper.innerHTML =
          `<input type="text" id="engraving-line-${i}" name="properties[Line ${i}]" ` +
          `class="product-customizer__input" maxlength="${targetChars}" ` +
          `placeholder="Line ${i}" data-engraving-input autocomplete="off">` +
          `<span class="product-customizer__char-count engraving_char_count" data-char-count>` +
          `<span data-current>0</span>/${targetChars}</span>`;
        linesContainer.appendChild(wrapper);
      }

      // Update maxlength + counter text on all remaining wrappers
      Array.from(linesContainer.querySelectorAll('.product-customizer__input-wrapper')).forEach((wrapper, i) => {
        const input   = wrapper.querySelector('input');
        const counter = wrapper.querySelector('[data-char-count]');
        const current = wrapper.querySelector('[data-current]');
        if (input) {
          input.maxLength   = targetChars;
          input.id          = `engraving-line-${i + 1}`;
          input.name        = `properties[Line ${i + 1}]`;
          input.placeholder = `Line ${i + 1}`;
        }
        if (counter && current) {
          counter.lastChild.textContent = `/${targetChars}`;
        }
      });

      // Re-run counters and apply-all for the refreshed inputs
      initCharacterCounters();
      initApplyToAllLines();
    }
  }

  /**
   * Form validation before submit
   */
  function initFormValidation() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    productForm.addEventListener('submit', (e) => {
      // Check required ribbon selection
      const ribbonSelect = document.getElementById('ribbon-select');
      if (ribbonSelect && ribbonSelect.required && !ribbonSelect.value) {
        e.preventDefault();
        ribbonSelect.focus();
        ribbonSelect.classList.add('product-customizer__select--error');
        alert('Please select a ribbon option.');
        return false;
      }

      // All validation passed
      return true;
    });

    // Remove error class on change
    const selects = document.querySelectorAll('.product-customizer__select');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        select.classList.remove('product-customizer__select--error');
      });
    });
  }

})();
